import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { useIsEnterpriseAdmin } from '../hooks/useIsEnterpriseAdmin';
import HubPageShell from '../components/home/HubPageShell';
import PlanWaitlistButton from '../components/plans/PlanWaitlistButton';
import styles from './UpgradePage.module.css';

const TIER_LABELS = {
  individual_basic: 'Basic',
  individual_pro: 'Pro',
  team_basic: 'Basic',
  team_pro: 'Pro',
  teams: 'Teams',
};

const BASIC_VALUE = 'Your card and your contacts, ready to use today.';
const PRO_VALUE = 'Meet the right people. Stay informed. Keep every opportunity moving.';
const TEAMS_VALUE = 'Build a company network that stays with your business.';

const BASIC_FEATURES = [
  'A digital card you can share by tap or link',
  'Unlimited contacts, with private notes',
  'Link and activate a physical Velox card',
  'A Market Intelligence headline matched to your interests',
];

const PRO_FEATURES = [
  'Everything in Basic',
  'Networking opportunities tailored to your industry and interests, and which people in your network are attending',
  'A personalised market briefing by email each week',
  'A daily AI summary of developments relevant to you, ready to refresh when you need it',
  'Automated follow-ups, with a place to manage them',
];

const TEAMS_FEATURES = [
  'A central dashboard for the company',
  'Company contacts for authorised managers, so business relationships are not left only on personal accounts or paper cards',
  'Company-owned accounts and roles, so a successor can inherit the relevant business contacts',
  'Automated organisation charting of those relationships',
  'Networking activity across the team: cards shared and received, meetings booked, and how people compare',
  'Which events and activities lead to useful business outcomes',
  'Order custom-designed metal NFC cards, with design support or your own artwork',
];

const MEMBER_PRO_FEATURES = PRO_FEATURES.filter((item) => item !== 'Everything in Basic');

function money(pence) {
  if (pence === null || pence === undefined) return '—';
  const pounds = pence / 100;
  return Number.isInteger(pounds) ? `£${pounds}` : `£${pounds.toFixed(2)}`;
}

function currentPlanExplanation(plan) {
  if (plan?.source === 'teams_licence') {
    return 'Pro is active on this account because a Teams licence is assigned to it. It is not a separate personal Pro subscription.';
  }
  if (plan?.source === 'both') {
    return 'Personal Pro is active, and a Teams licence is assigned as well.';
  }
  if (plan?.label === 'Pro') {
    return 'Personal Pro is active on this account.';
  }
  return 'Basic is active. Pro and Teams are not included on this account.';
}

function CurrentPlanBanner({ plan }) {
  if (!plan?.label) return null;
  const name = plan.detail ? `${plan.label} · ${plan.detail}` : plan.label;
  return (
    <section className={styles.currentBanner} aria-label="Your current plan">
      <p className={styles.currentKicker}>Your plan</p>
      <p className={styles.currentName}>{name}</p>
      <p className={styles.currentExplain}>{currentPlanExplanation(plan)}</p>
    </section>
  );
}

/**
 * Customer-facing current plan. Never print personalTier alone when Pro is
 * earned via a Teams licence — that reads as “Basic” incorrectly.
 */
export function resolveCurrentPlanDisplay({
  displayPlan,
  proSource,
  isPro,
  personalTier,
  effectiveTier,
} = {}) {
  if (displayPlan?.label) {
    return {
      label: displayPlan.label,
      detail: displayPlan.detail || null,
      source: displayPlan.source || proSource || null,
    };
  }
  if (proSource === 'teams_licence') {
    return { label: 'Pro', detail: 'via Teams', source: 'teams_licence' };
  }
  if (proSource === 'both') {
    return { label: 'Pro', detail: 'Personal and Teams', source: 'both' };
  }
  if (isPro || personalTier === 'individual_pro' || proSource === 'individual') {
    return { label: 'Pro', detail: null, source: proSource || 'individual' };
  }
  return {
    label: TIER_LABELS[effectiveTier] || TIER_LABELS[personalTier] || 'Basic',
    detail: null,
    source: null,
  };
}

function CheckoutNote({ chargingEnabled, message }) {
  if (message) return <p className={styles.cardNote}>{message}</p>;
  if (chargingEnabled) return null;
  return (
    <p className={styles.cardNote}>
      Checkout opens when subscription billing goes live. A quote is not a charge.
    </p>
  );
}

function IndividualPlans({
  individual,
  teams,
  isPro,
  proSource,
  cadence,
  setCadence,
  vatNote,
  chargingEnabled,
  onBuyPro,
  onBuyTeams,
  busy,
  highlightTeams = false,
  highlightPro = false,
  proAvailable = false,
  teamsAvailable = false,
  proJoined = false,
  teamsJoined = false,
}) {
  const annualSavingPence = individual.proMonthlyPence * 12 - individual.proAnnualPence;
  const annualMonthlyEquivalentPence = Math.round(individual.proAnnualPence / 12);
  const monthsFree =
    individual.proMonthlyPence > 0 ? Math.round(annualSavingPence / individual.proMonthlyPence) : 0;
  const proPlan = cadence === 'annual' ? 'pro_annual' : 'pro_monthly';
  const teamsMonthlyPence = teams?.basePence || 2500;
  const teamsAnnualPence = teams?.baseAnnualPence || teamsMonthlyPence * 10;
  const teamsAnnualMonthlyEquivalentPence = Math.round(teamsAnnualPence / 12);
  const teamsAnnualSavingPence = teamsMonthlyPence * 12 - teamsAnnualPence;
  const teamsMonthsFree =
    teamsMonthlyPence > 0 ? Math.round(teamsAnnualSavingPence / teamsMonthlyPence) : 0;
  const teamsCardRef = useRef(null);
  const proCardRef = useRef(null);

  useEffect(() => {
    const node = highlightTeams ? teamsCardRef.current : highlightPro ? proCardRef.current : null;
    if (!node || typeof node.scrollIntoView !== 'function') return undefined;
    const timer = window.setTimeout(() => {
      node.scrollIntoView({ block: 'nearest' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [highlightPro, highlightTeams]);

  return (
    <>
      <section className={styles.section} aria-labelledby="plans-heading">
        <div className={styles.sectionHead}>
          <div>
            <h2 id="plans-heading" className={styles.sectionTitle}>
              Plans
            </h2>
            {highlightTeams || (proAvailable && teamsAvailable) ? (
              <p className={styles.sectionIntro}>
                {highlightTeams
                  ? teamsAvailable
                    ? 'A workspace starts on Teams.'
                    : 'Teams is coming soon. Join the waitlist to hear when a company workspace opens.'
                  : 'Personal Pro, or a company workspace on Teams.'}
              </p>
            ) : null}
          </div>

          <div className={styles.cadence} role="group" aria-label="Billing period">
            <button
              type="button"
              className={cadence === 'monthly' ? styles.cadenceActive : styles.cadenceBtn}
              aria-pressed={cadence === 'monthly'}
              onClick={() => setCadence('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={cadence === 'annual' ? styles.cadenceActive : styles.cadenceBtn}
              aria-pressed={cadence === 'annual'}
              onClick={() => setCadence('annual')}
            >
              Annual
              {annualSavingPence > 0 ? (
                <span className={styles.cadenceSave}>Save {money(annualSavingPence)}</span>
              ) : null}
            </button>
          </div>
        </div>

        <div className={styles.cards}>
          <article
            id="plan-basic"
            className={`${styles.card} ${!isPro ? styles.cardCurrent : ''}`}
            data-current={!isPro ? 'true' : undefined}
          >
            <div className={styles.cardHead}>
              <h3 className={styles.cardTitle}>Basic</h3>
              {!isPro ? <span className={styles.badge}>Your plan</span> : null}
            </div>
            <p className={styles.price}>Free</p>
            <p className={styles.value}>{BASIC_VALUE}</p>
            <p className={styles.priceAlt}>No subscription.</p>
            <ul className={styles.features}>
              {BASIC_FEATURES.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {!isPro ? (
              <p className={styles.cardNote}>Your current plan.</p>
            ) : (
              <p className={styles.cardNote}>Available if you leave Pro later.</p>
            )}
          </article>

          <article
            ref={proCardRef}
            id="plan-pro"
            className={`${styles.card} ${isPro ? styles.cardCurrent : ''} ${
              !highlightTeams && (isPro || highlightPro) ? styles.cardFeatured : ''
            }`}
            data-current={isPro ? 'true' : undefined}
            data-highlighted={!highlightTeams && (isPro || highlightPro) ? 'true' : undefined}
          >
            <div className={styles.cardHead}>
              <h3 className={styles.cardTitle}>Pro</h3>
              {isPro ? (
                <span className={styles.badge}>Your plan</span>
              ) : proAvailable ? (
                highlightTeams ? null : (
                  <span className={styles.badge}>{cadence === 'annual' ? 'Best value' : 'Popular'}</span>
                )
              ) : (
                <span className={styles.badge}>Coming soon</span>
              )}
            </div>

            {cadence === 'annual' ? (
              <>
                <p className={styles.price}>
                  {money(annualMonthlyEquivalentPence)}
                  <span className={styles.per}>/month</span>
                </p>
                <p className={styles.priceAlt}>
                  Billed {money(individual.proAnnualPence)} once a year
                  {monthsFree > 0 ? ` · ${monthsFree} months free` : ''}
                </p>
              </>
            ) : (
              <>
                <p className={styles.price}>
                  {money(individual.proMonthlyPence)}
                  <span className={styles.per}>/month</span>
                </p>
                <p className={styles.priceAlt}>
                  or {money(individual.proAnnualPence)}/year
                  {annualSavingPence > 0 ? ` · save ${money(annualSavingPence)}` : ''}
                </p>
              </>
            )}

            <p className={styles.value}>{PRO_VALUE}</p>
            <ul className={styles.features}>
              {PRO_FEATURES.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            {isPro ? (
              <p className={styles.cardNote}>
                {proSource === 'teams_licence'
                  ? 'Your plan, through an assigned Teams licence.'
                  : 'Your plan.'}
              </p>
            ) : proAvailable ? (
              <>
                {chargingEnabled ? (
                  <button
                    type="button"
                    className={styles.cta}
                    disabled={busy}
                    onClick={() => onBuyPro(proPlan)}
                  >
                    Pay with Stripe
                  </button>
                ) : null}
                <CheckoutNote chargingEnabled={chargingEnabled} />
              </>
            ) : (
              <PlanWaitlistButton plan="pro" joined={proJoined} />
            )}
          </article>

          <article
            ref={teamsCardRef}
            id="plan-teams"
            className={`${styles.card} ${highlightTeams ? styles.cardFeatured : ''}`}
            data-highlighted={highlightTeams ? 'true' : undefined}
          >
            <div className={styles.cardHead}>
              <h3 className={styles.cardTitle}>Teams</h3>
              {teamsAvailable ? (
                highlightTeams ? <span className={styles.badge}>Workspace</span> : null
              ) : (
                <span className={styles.badge}>Coming soon</span>
              )}
            </div>
            {cadence === 'annual' ? (
              <>
                <p className={styles.price}>
                  {money(teamsAnnualMonthlyEquivalentPence)}
                  <span className={styles.per}>/month</span>
                </p>
                <p className={styles.priceAlt}>
                  Billed {money(teamsAnnualPence)} once a year
                  {teamsMonthsFree > 0 ? ` · ${teamsMonthsFree} months free` : ''}
                </p>
              </>
            ) : (
              <>
                <p className={styles.price}>
                  {money(teamsMonthlyPence)}
                  <span className={styles.per}>/month</span>
                </p>
                <p className={styles.priceAlt}>
                  or {money(teamsAnnualPence)}/year
                  {teamsAnnualSavingPence > 0 ? ` · save ${money(teamsAnnualSavingPence)}` : ''}
                </p>
              </>
            )}
            <p className={styles.value}>{TEAMS_VALUE}</p>
            <p className={styles.priceAlt}>
              A workspace includes {teams?.includedMembers || 5} member slots and{' '}
              {teams?.includedProLicences || 1} Pro licence to assign. Cards are ordered separately.
            </p>
            <ul className={styles.features}>
              {TEAMS_FEATURES.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className={styles.priceAlt}>
              Extra member from {money(teams?.extraMemberPence?.members_6_50 || 500)}/month · extra Pro
              licence {money(teams?.extraProPence || 400)}/month
            </p>
            {isPro ? (
              <p className={styles.cardNote}>
                Teams includes a Pro licence. Keep personal Pro, or allocate the Teams licence to
                someone else.
              </p>
            ) : null}
            {teamsAvailable ? (
              <button type="button" className={styles.cta} disabled={busy} onClick={onBuyTeams}>
                {cadence === 'annual' ? 'Continue with annual Teams' : 'Continue with monthly Teams'}
              </button>
            ) : (
              <PlanWaitlistButton plan="teams" joined={teamsJoined} />
            )}
            <Link className={styles.secondaryCta} to="/enterprise">
              Talk to us about Teams
            </Link>
            {teamsAvailable ? <CheckoutNote chargingEnabled={chargingEnabled} /> : null}
          </article>
        </div>
      </section>

      <CommerceFooter vatNote={vatNote} />
    </>
  );
}

function TeamAdminPlans({ plans, vatNote }) {
  const teams = plans?.teams || plans?.team || {};
  return (
    <>
      <section className={styles.section} aria-labelledby="team-heading">
        <div className={styles.sectionHead}>
          <div>
            <h2 id="team-heading" className={styles.sectionTitle}>
              Teams
            </h2>
            <p className={styles.sectionIntro}>
              {money(teams.basePence || 2500)}/month, or {money(teams.baseAnnualPence || 25000)}/year,
              includes {teams.includedMembers || 5} members and {teams.includedProLicences || 1} Pro
              licence. Extra people and licences are billed separately. Cards stay a one-off order.
            </p>
          </div>
        </div>

        <div className={styles.cards}>
          <article className={styles.card}>
            <h3 className={styles.cardTitle}>Member capacity</h3>
            <p className={styles.priceAlt}>
              Extra people 6–50 at {money(teams.extraMemberPence?.members_6_50 || 500)}/month. People
              51+ at {money(teams.extraMemberPence?.members_51_plus || 400)}/month.
            </p>
          </article>
          <article className={styles.card}>
            <h3 className={styles.cardTitle}>Pro licences</h3>
            <p className={styles.priceAlt}>
              Extra Pro licences {money(teams.extraProPence || 400)}/month each. A Pro person uses one
              member slot plus one licence.
            </p>
          </article>
        </div>

        <div className={styles.footerLinks}>
          <Link className={styles.cta} to="/order?kind=team">
            Order cards
          </Link>
          <Link className={styles.secondaryCta} to="/billing">
            Billing
          </Link>
        </div>
      </section>
      <CommerceFooter vatNote={vatNote} includeQuote />
    </>
  );
}

function MemberOnPro() {
  return (
    <section className={styles.section} aria-labelledby="member-plan-heading">
      <article className={`${styles.card} ${styles.cardFeatured}`}>
        <div className={styles.cardHead}>
          <h2 id="member-plan-heading" className={styles.cardTitle}>
            Pro
          </h2>
          <span className={styles.badge}>Current</span>
        </div>
        <p className={styles.value}>{PRO_VALUE}</p>
        <p className={styles.priceAlt}>Insights is unlocked on your company licence.</p>
        <ul className={styles.features}>
          {MEMBER_PRO_FEATURES.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </article>
    </section>
  );
}

function MemberNeedsPro({
  individual,
  cadence,
  setCadence,
  chargingEnabled,
  busy,
  onBuyPro,
  proAvailable = false,
  proJoined = false,
}) {
  const annualSavingPence = individual.proMonthlyPence * 12 - individual.proAnnualPence;
  const annualMonthlyEquivalentPence = Math.round(individual.proAnnualPence / 12);
  const proPlan = cadence === 'annual' ? 'pro_annual' : 'pro_monthly';

  return (
    <section className={styles.section} aria-labelledby="member-plan-heading">
      <div className={styles.sectionHead}>
        <div>
          <h2 id="member-plan-heading" className={styles.sectionTitle}>
            Two ways to get Pro
          </h2>
          <p className={styles.sectionIntro}>
            {proAvailable
              ? 'Ask your company owner or admin to assign a Pro licence, or buy Individual Pro for yourself. Either one opens the full briefing and networking. A Teams seat on its own does not.'
              : 'Ask your company owner or admin to assign a Pro licence, or join the Pro waitlist. A Teams seat on its own does not include Pro.'}
          </p>
        </div>
        <div className={styles.cadence} role="group" aria-label="Pro billing period">
          <button
            type="button"
            className={cadence === 'monthly' ? styles.cadenceActive : styles.cadenceBtn}
            aria-pressed={cadence === 'monthly'}
            onClick={() => setCadence('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={cadence === 'annual' ? styles.cadenceActive : styles.cadenceBtn}
            aria-pressed={cadence === 'annual'}
            onClick={() => setCadence('annual')}
          >
            Annual
            {annualSavingPence > 0 ? (
              <span className={styles.cadenceSave}>Save {money(annualSavingPence)}</span>
            ) : null}
          </button>
        </div>
      </div>

      <div className={styles.cards}>
        <article className={styles.card}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Company licence</h3>
          </div>
          <p className={styles.priceAlt}>
            Your owner or admin assigns a Pro licence to your seat. Nothing extra to buy, and you
            do not need a separate Individual Pro subscription as well.
          </p>
          <ul className={styles.features}>
            {MEMBER_PRO_FEATURES.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className={styles.cardNote}>Ask your company owner or admin if you need Pro.</p>
        </article>

        <article className={`${styles.card} ${styles.cardFeatured}`}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Individual Pro</h3>
          </div>
          {cadence === 'annual' ? (
            <>
              <p className={styles.price}>
                {money(annualMonthlyEquivalentPence)}
                <span className={styles.per}>/month</span>
              </p>
              <p className={styles.priceAlt}>Billed {money(individual.proAnnualPence)} once a year</p>
            </>
          ) : (
            <>
              <p className={styles.price}>
                {money(individual.proMonthlyPence)}
                <span className={styles.per}>/month</span>
              </p>
              <p className={styles.priceAlt}>or {money(individual.proAnnualPence)}/year</p>
            </>
          )}
          <p className={styles.value}>{PRO_VALUE}</p>
          <ul className={styles.features}>
            {MEMBER_PRO_FEATURES.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {proAvailable ? (
            <>
              <button
                type="button"
                className={styles.cta}
                disabled={busy}
                onClick={() => onBuyPro(proPlan)}
              >
                {cadence === 'annual' ? 'Continue with annual Pro' : 'Continue with monthly Pro'}
              </button>
              {chargingEnabled ? (
                <button
                  type="button"
                  className={styles.secondaryCta}
                  disabled={busy}
                  onClick={() => onBuyPro(proPlan)}
                >
                  Pay with Stripe
                </button>
              ) : null}
              <CheckoutNote chargingEnabled={chargingEnabled} />
            </>
          ) : (
            <PlanWaitlistButton plan="pro" joined={proJoined} />
          )}
        </article>
      </div>
    </section>
  );
}

function CommerceFooter({ vatNote, includeQuote = false }) {
  return (
    <footer className={styles.footer}>
      {vatNote ? <p className={styles.vat}>{vatNote}</p> : null}
      <p className={styles.footerLinks}>
        {includeQuote ? <Link to="/order">Build a quote</Link> : null}
        <Link to="/billing">Billing status</Link>
      </p>
    </footer>
  );
}

export default function UpgradePage() {
  const {
    effectiveTier,
    isPro,
    isLoading: entLoading,
    displayPlan,
    proSource,
    personalTier,
  } = useEntitlements();
  const { isEnterpriseAdmin, isEnterpriseUser, loading: roleLoading } = useIsEnterpriseAdmin();
  const currentPlan = resolveCurrentPlanDisplay({
    displayPlan,
    proSource,
    isPro,
    personalTier,
    effectiveTier,
  });
  const [searchParams] = useSearchParams();
  const focus = searchParams.get('focus');
  const focusTeams = focus === 'teams';
  const focusPro = focus === 'pro';
  const [catalog, setCatalog] = useState(null);
  const [joined, setJoined] = useState({ pro: false, teams: false });
  const [error, setError] = useState(null);
  const [cadence, setCadence] = useState('annual');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [chargingEnabled, setChargingEnabled] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [showTeamsForm, setShowTeamsForm] = useState(false);

  const needsCatalog = !roleLoading && !(isEnterpriseUser && isPro);

  useEffect(() => {
    if (!needsCatalog) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [catalogRes, statusRes, mineRes] = await Promise.all([
          api.get('/api/entitlements/catalog'),
          api.get('/api/billing/status').catch(() => null),
          api.get('/api/plan-waitlist/mine').catch(() => null),
        ]);
        if (cancelled) return;
        setCatalog(catalogRes.data);
        setChargingEnabled(!!statusRes?.data?.stripe?.chargingEnabled);
        const mineJoined = mineRes?.data?.joined;
        setJoined({
          pro: mineJoined?.pro === true,
          teams: mineJoined?.teams === true,
        });
      } catch {
        if (!cancelled) setError('We could not load plans just now. Please try again shortly.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsCatalog]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setNotice('Payment received. Access updates when Stripe confirms the subscription.');
    }
    if (checkout === 'cancelled') {
      setNotice('Checkout was cancelled. Nothing was charged.');
    }
  }, [searchParams]);

  const plans = catalog?.plans;
  const individual = plans?.individual;
  const teams = plans?.teams || plans?.team;
  const proAvailable = catalog?.availability?.pro === true;
  const teamsAvailable = catalog?.availability?.teams === true;
  const vatNote = useMemo(() => {
    if (!plans) return null;
    return plans.vatMode === 'inclusive'
      ? 'Prices include VAT.'
      : 'Prices exclude VAT, which is added at checkout.';
  }, [plans]);

  const loading = roleLoading || entLoading || (needsCatalog && !catalog && !error);

  const startCheckout = async (body) => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.post('/api/billing/checkout', body);
      if (res.data?.url) {
        window.location.assign(res.data.url);
        return;
      }
      setNotice('Checkout is ready, but no payment page was returned.');
    } catch (e) {
      setNotice(
        e.response?.data?.message ||
          'Checkout is not enabled yet. A quote is not a charge.'
      );
    } finally {
      setBusy(false);
    }
  };

  let title = 'Choose the network you want to build';
  let lead = '';
  if (isEnterpriseAdmin) {
    title = 'Teams capacity';
    lead = 'Member slots and Pro licences are billed separately from physical cards.';
  } else if (isEnterpriseUser) {
    title = isPro ? 'You are on Pro' : 'Your company plan';
    lead = isPro
      ? 'Your company licence already includes Pro.'
      : proAvailable
        ? 'A Pro licence from your company unlocks the briefing and networking. You can also buy Individual Pro yourself.'
        : 'A Pro licence from your company unlocks Pro. You can also join the Pro waitlist.';
  } else if (focusTeams) {
    lead = teamsAvailable
      ? 'Teams is the plan that creates a company workspace.'
      : "A company workspace starts on Teams. Join the waitlist and we'll email you when it opens.";
  } else if (proAvailable && teamsAvailable) {
    lead = 'Personal Pro, or a company workspace on Teams.';
  }

  return (
    <HubPageShell title="Plans">
      <div className={styles.page}>
        <header className={styles.hero}>
          <h1 className={styles.title}>{title}</h1>
          {lead ? <p className={styles.lead}>{lead}</p> : null}
          {!entLoading && !roleLoading ? <CurrentPlanBanner plan={currentPlan} /> : null}
          {notice ? (
            <p className={styles.cardNote} role="status">
              {notice}
            </p>
          ) : null}
        </header>

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        {loading ? <p className={styles.lead}>Loading plans…</p> : null}

        {!loading && isEnterpriseUser && isPro ? <MemberOnPro /> : null}
        {!loading && isEnterpriseUser && !isPro && individual ? (
          <MemberNeedsPro
            individual={individual}
            cadence={cadence}
            setCadence={setCadence}
            chargingEnabled={chargingEnabled}
            busy={busy}
            proAvailable={proAvailable}
            proJoined={joined.pro}
            onBuyPro={(plan) => startCheckout({ kind: 'individual_pro', plan })}
          />
        ) : null}

        {!loading && isEnterpriseAdmin && plans ? (
          <TeamAdminPlans plans={plans} vatNote={vatNote} />
        ) : null}

        {!loading && !isEnterpriseAdmin && !isEnterpriseUser && individual ? (
          <>
            {showTeamsForm && teamsAvailable ? (
              <section className={styles.section}>
                <article className={styles.card}>
                  <h2 className={styles.cardTitle}>Create your workspace</h2>
                  <p className={styles.priceAlt}>
                    {cadence === 'annual'
                      ? `${money(teams?.baseAnnualPence || 25000)}/year`
                      : `${money(teams?.basePence || 2500)}/month`}{' '}
                    · 5 members · 1 Pro licence
                  </p>
                  <label className={styles.field} htmlFor="company-name">
                    Company name
                    <input
                      id="company-name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Acme Ltd"
                      autoComplete="organization"
                    />
                  </label>
                  <button
                    type="button"
                    className={styles.cta}
                    disabled={busy || !companyName.trim()}
                    onClick={() =>
                      startCheckout({
                        kind: 'teams',
                        plan: cadence === 'annual' ? 'teams_annual' : 'teams_monthly',
                        companyName: companyName.trim(),
                        members: teams?.includedMembers || 5,
                        proLicences: teams?.includedProLicences || 1,
                      })
                    }
                  >
                    Continue to payment
                  </button>
                  <button
                    type="button"
                    className={styles.secondaryCta}
                    onClick={() => setShowTeamsForm(false)}
                  >
                    Back
                  </button>
                  <CheckoutNote chargingEnabled={chargingEnabled} />
                </article>
              </section>
            ) : (
              <IndividualPlans
                individual={individual}
                teams={teams}
                isPro={isPro}
                proSource={proSource}
                cadence={cadence}
                setCadence={setCadence}
                vatNote={vatNote}
                chargingEnabled={chargingEnabled}
                busy={busy}
                highlightTeams={focusTeams}
                highlightPro={focusPro}
                proAvailable={proAvailable}
                teamsAvailable={teamsAvailable}
                proJoined={joined.pro}
                teamsJoined={joined.teams}
                onBuyPro={(plan) => startCheckout({ kind: 'individual_pro', plan })}
                onBuyTeams={() => setShowTeamsForm(true)}
              />
            )}
          </>
        ) : null}
      </div>
    </HubPageShell>
  );
}
